/**
 * OTA (Over-The-Air) update manager.
 * Uses @capgo/capacitor-updater on native platforms to download and apply
 * web bundle updates from our self-hosted Supabase backend.
 */
import { Capacitor } from '@capacitor/core';
import { APP_VERSION, BUILD_NUMBER } from './version';

interface OtaUpdateInfo {
  update: boolean;
  version?: string;
  build_number?: number;
  url?: string;
  checksum?: string;
  release_notes?: string;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const OTA_STORAGE_KEY = 'ota_updated_version';
const OTA_NOTES_KEY = 'ota_release_notes';
const OTA_CHECK_COOLDOWN_KEY = 'ota_last_check';
const CHECK_COOLDOWN_MS = 3 * 60 * 1000; // 3 minutes cooldown between checks
const OTA_RETRY_KEY = 'ota_retry_count';
const MAX_RETRIES = 3;
const RETRY_INTERVALS = [30_000, 60_000, 120_000]; // 30s, 1min, 2min
const OTA_CRASH_GUARD_KEY = 'ota_crash_guard';
const OTA_CRASH_COUNT_KEY = 'ota_crash_count';
const MAX_CRASH_COUNT = 3; // After 3 consecutive crash-on-set, stop applying OTA

/**
 * Crash loop detection.
 * Before calling CapacitorUpdater.set() we write a guard flag.
 * After the app successfully starts (notifyAppReady in main.tsx), we clear it.
 * If the guard flag is still present at the NEXT launch, it means the previous
 * set() caused a crash/rollback.  After MAX_CRASH_COUNT consecutive crashes we
 * disable OTA entirely until the user manually updates the native binary.
 */
function isOtaCrashLoopDetected(): boolean {
  try {
    const count = parseInt(localStorage.getItem(OTA_CRASH_COUNT_KEY) || '0', 10);
    return count >= MAX_CRASH_COUNT;
  } catch {
    return false;
  }
}

function markOtaSetAttempt(): void {
  try {
    localStorage.setItem(OTA_CRASH_GUARD_KEY, String(Date.now()));
  } catch {}
}

/** Call once at app startup to detect and count crash loops */
export function checkOtaCrashGuard(): void {
  try {
    const guard = localStorage.getItem(OTA_CRASH_GUARD_KEY);
    if (guard) {
      // Guard was set but never cleared → previous set() caused a crash
      const count = parseInt(localStorage.getItem(OTA_CRASH_COUNT_KEY) || '0', 10) + 1;
      localStorage.setItem(OTA_CRASH_COUNT_KEY, String(count));
      localStorage.removeItem(OTA_CRASH_GUARD_KEY);
      console.warn(`[OTA] Crash loop detected — count: ${count}/${MAX_CRASH_COUNT}`);
    } else {
      // App started normally → reset crash counter
      localStorage.removeItem(OTA_CRASH_COUNT_KEY);
    }
  } catch {}
}

/** Clear the guard flag — call after notifyAppReady succeeds */
export function clearOtaCrashGuard(): void {
  try {
    localStorage.removeItem(OTA_CRASH_GUARD_KEY);
  } catch {}
}

async function cleanupOldBundles(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    const [{ bundle: current }, { bundles }] = await Promise.all([
      CapacitorUpdater.current(),
      CapacitorUpdater.list(),
    ]);

    const staleBundles = bundles.filter((bundle) => bundle.id && bundle.id !== current.id);

    await Promise.all(staleBundles.map(async (bundle) => {
      try {
        await CapacitorUpdater.delete({ id: bundle.id });
      } catch (error) {
        console.warn(`[OTA] Failed to delete old bundle ${bundle.id}:`, error);
      }
    }));

    if (staleBundles.length > 0) {
      console.log(`[OTA] Deleted ${staleBundles.length} old bundle(s)`);
    }
  } catch (error) {
    console.warn('[OTA] Old bundle cleanup skipped:', error);
  }
}

/**
 * Check for OTA updates and apply if available.
 * Only runs on native platforms (iOS/Android).
 * On web, this is a no-op.
 */
export async function checkAndApplyOtaUpdate(
  onStatus?: (status: string, version?: string) => void,
  skipCooldown = false,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // Crash loop protection (Android only): if the last N OTA set() calls all
  // resulted in crashes, stop trying to apply OTA updates entirely.
  const isAndroid = Capacitor.getPlatform() === 'android';
  if (isAndroid && isOtaCrashLoopDetected()) {
    console.warn('[OTA] Skipping update — crash loop detected, please update the native binary');
    onStatus?.('idle');
    return;
  }

  // Cooldown: don't check too frequently
  if (!skipCooldown) {
    try {
      const lastCheck = localStorage.getItem(OTA_CHECK_COOLDOWN_KEY);
      if (lastCheck && Date.now() - parseInt(lastCheck, 10) < CHECK_COOLDOWN_MS) {
        console.log('[OTA] Skipping check — cooldown active');
        return;
      }
    } catch {}
  }

  try {
    const platform = Capacitor.getPlatform();

    onStatus?.('checking');
    console.log(`[OTA] Checking updates — v${APP_VERSION} build ${BUILD_NUMBER} (${platform})`);

    const checkUrl = `${SUPABASE_URL}/functions/v1/check-ota-update?version=${APP_VERSION}&build=${BUILD_NUMBER}&platform=${platform}`;
    const res = await fetch(checkUrl, {
      headers: { apikey: SUPABASE_ANON_KEY },
    });

    try { localStorage.setItem(OTA_CHECK_COOLDOWN_KEY, String(Date.now())); } catch {}

    if (!res.ok) {
      console.warn('[OTA] Update check failed:', res.status, await res.text().catch(() => ''));
      onStatus?.('error');
      return;
    }

    const info: OtaUpdateInfo = await res.json();
    console.log('[OTA] Check response:', JSON.stringify(info));

    if (!info.update || !info.url) {
      console.log('[OTA] App is up to date');
      onStatus?.('idle');
      return;
    }

    console.log(`[OTA] Update available: v${info.version} (build ${info.build_number})`);
    onStatus?.('downloading', info.version);

    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');

    console.log(`[OTA] Starting download from: ${info.url}`);
    const downloadStart = Date.now();

    const bundle = await CapacitorUpdater.download({
      url: info.url,
      version: info.version!,
    });

    console.log(`[OTA] Bundle downloaded in ${((Date.now() - downloadStart) / 1000).toFixed(1)}s: ${bundle.id}`);
    onStatus?.('applying', info.version);

    try {
      localStorage.setItem(OTA_STORAGE_KEY, info.version!);
      if (info.release_notes) {
        localStorage.setItem(OTA_NOTES_KEY, info.release_notes);
      }
      localStorage.setItem('ota_clear_cache', '1');
    } catch {}

    // Mark that we're about to call set() — if the app crashes before clearing
    // this flag, the next launch will detect a crash loop. (Android only)
    if (isAndroid) {
      markOtaSetAttempt();
    }

    // Don't reload during onboarding — the user might lose unsaved selections.
    // Just keep the downloaded bundle; it will be applied on the next app start.
    const currentPath = window.location.pathname;
    if (currentPath === '/onboarding') {
      console.log('[OTA] Bundle downloaded but deferring set() — user is on onboarding');
      onStatus?.('idle');
      return;
    }

    await CapacitorUpdater.set(bundle);
    // App will reload here
  } catch (e: any) {
    console.error('[OTA] Update failed:', e?.message || e);
    onStatus?.('error');

    // Schedule automatic retry
    scheduleRetry(onStatus);
  }
}

function scheduleRetry(
  onStatus?: (status: string, version?: string) => void,
): void {
  try {
    const retryCount = parseInt(localStorage.getItem(OTA_RETRY_KEY) || '0', 10);
    if (retryCount >= MAX_RETRIES) {
      console.log(`[OTA] Max retries (${MAX_RETRIES}) reached — will retry on next app resume`);
      localStorage.removeItem(OTA_RETRY_KEY);
      return;
    }

    const delay = RETRY_INTERVALS[retryCount] ?? RETRY_INTERVALS[RETRY_INTERVALS.length - 1];
    localStorage.setItem(OTA_RETRY_KEY, String(retryCount + 1));
    console.log(`[OTA] Scheduling retry ${retryCount + 1}/${MAX_RETRIES} in ${delay / 1000}s`);

    setTimeout(() => {
      checkAndApplyOtaUpdate((status, version) => {
        if (status !== 'error') {
          // Reset retry counter on any non-error outcome
          try { localStorage.removeItem(OTA_RETRY_KEY); } catch {}
        }
        onStatus?.(status, version);
      }, true); // skipCooldown since this is a scheduled retry
    }, delay);
  } catch {}
}

/** Reset the retry counter — call when the app resumes from background */
export function resetOtaRetryCounter(): void {
  try { localStorage.removeItem(OTA_RETRY_KEY); } catch {}
}

/**
 * Notify the updater plugin that the current bundle is working fine.
 * Call this only after the app has stayed stable long enough to trust the bundle.
 * If not called within the timeout, the plugin should rollback to the previous version.
 */
export async function notifyOtaReady(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    await CapacitorUpdater.notifyAppReady();

    if (Capacitor.getPlatform() === 'android') {
      clearOtaCrashGuard();
    }

    console.log('[OTA] App ready confirmed after stability delay');
    await cleanupOldBundles();
  } catch (e) {
    console.error('[OTA] notifyAppReady failed:', e);
  }
}
